import { expireSession } from '@/auth/session'

export type AuthStartResp = {
  ok: boolean
  sent?: boolean
  dev_token?: string | null
  invite_required?: boolean
}

export type AuthLogoutResp = {
  ok: boolean
}

export type AuthVerifyResp = {
  ok: boolean
  session_token: string
  account: any
  org: any
  default_watchlist_id?: string | null
  org_setup_required?: boolean
  onboarding_completed?: boolean
}

export type InviteAcceptResp = {
  ok: boolean
  org: any
  role?: string | null
  default_watchlist_id?: string | null
  org_setup_required?: boolean
  onboarding_completed?: boolean
}

export type InvitePreviewResp = {
  ok: boolean
  invite_code: string
  org?: any | null
  role?: string | null
  email_hint?: string | null
  requires_new_org?: boolean
}

export type AskUsage = {
  org: {
    running: number
    last_hour: number
    max_concurrent: number
    max_per_hour: number
  }
  account: {
    running: number
    last_hour: number
    max_per_hour: number
  } | null
}

export type AskLimits = {
  ask_enabled: boolean
  ask_set_enabled: boolean
  max_concurrent_jobs: number
  max_jobs_per_hour: number
  per_account_max_jobs_per_hour: number
  set_max_concurrent_jobs: number
  set_max_jobs_per_hour: number
  set_max_sources: number
  set_max_questions: number
}

export type ClientMeta = Record<string, any>

export type MeResp = {
  account: any
  active_org_id: string
  active_org?: any | null
  active_org_role?: string | null
  orgs: any[]
  is_cenva_staff?: boolean
  cenva_access_mode?: string
  ask_usage?: AskUsage | null
  ask_limits?: AskLimits | null
}

export type OrgSearchResp = {
  q: string
  count: number
  orgs: any[]
  is_staff?: boolean
  access_mode?: string
}

export type ActiveOrgSwitchResp = {
  ok: boolean
  active_org_id: string
  org?: any | null
  default_watchlist_id?: string | null
}

export type OrgUpdateResp = {
  ok: boolean
  org: any
}

export type Pack = {
  pack_id: string
  name: string
  description?: string | null
  is_public?: boolean
  kind: string
  rules_json?: any
}

export type WatchlistDetail = {
  watchlist: any
  items: any[]
}

export type WatchlistSummaryMover = {
  item_id: string
  label: string
  probability: number
  probability_prev: number | null
  delta: number | null
  as_of: string | null
  summary?: string | null
}

export type WatchlistSummary = {
  total_items: number
  items_with_forecasts: number
  forecasts_updated_24h: number
  avg_abs_edge: number | null
  biggest_movers: WatchlistSummaryMover[]
  recent_updates: WatchlistSummaryMover[]
}

/**
 * Stable reason codes the server emits when `needs_confirmation` is true.
 * UI chooses the primary confirmation-card header based on this code,
 * falling back to `needs_confirmation_message` (server-authored copy)
 * and finally to a generic string. Keep in sync with client_api.py.
 */
export type RefineReason = 'past_date' | 'categorical' | 'ambiguous' | 'low_confidence'

/** Stable slot identifiers the backend emits in copilot clarifications. */
export type CopilotSlot =
  | 'deadline'
  | 'subject'
  | 'threshold'
  | 'success_condition'
  | 'scope'
  | 'election'
  | 'outcome_form'
  | 'options'
  | string

/**
 * Stable option IDs the backend emits alongside human labels. Anything
 * not listed here just uses the slugified label; UI should treat those
 * as opaque.
 */
export type CopilotOptionId =
  | 'end_of_year'
  | 'specific_date'
  | 'specific_month_year'
  | 'official_announcement'
  | 'formal_launch'
  | 'regulatory_filing'
  | 'specific_threshold'
  | 'single_winner'
  | 'separate_binaries'
  | 'name_candidates'
  | 'victoria_au_state'
  | 'victoria_local'
  | 'victoria_other'
  | string

export type CopilotOptionItem = { id: CopilotOptionId; label: string }

export type CopilotClarification = {
  slot?: CopilotSlot
  prompt: string
  /** Legacy string list; prefer `option_items` when present. */
  options?: string[]
  option_items?: CopilotOptionItem[]
}

/**
 * One of N alternative resolution criteria the drafter generated to
 * pin down a flagged ambiguity (e.g. "combat forces only" vs "all
 * active-duty personnel" for a troop-deployment question). Each entry
 * is a self-contained criterion the user can one-click into the
 * Proposed resolution textarea.
 */
export type QuestionRewrite = {
  label: string
  question: string
  fixes?: string | null
}

export type ResolutionVariant = {
  label: string
  criterion: string
  addresses_ambiguity?: string | null
}

/**
 * Server-drafted concrete resolution criteria + sources. Emitted when
 * the LLM would otherwise hand back the generic "Binary yes/no resolution"
 * placeholder. `auto_applied=true` means the draft has already been
 * written into `analysis.resolution_criteria`; the UI should just
 * surface the fact and let the user tweak if they disagree. When
 * `auto_applied=false` the user must Accept (or edit) before the
 * question can be forecast.
 *
 * `alternative_variants` carries concrete scope alternatives the
 * drafter produced to pre-empt the user having to hand-write a
 * clarification. Rendered as chips under the textarea.
 */
export type ProposedResolution = {
  resolution_criteria: string
  resolution_sources: string[]
  success_event?: string | null
  confidence: number
  needs_clarification?: string | null
  auto_applied: boolean
  alternative_variants?: ResolutionVariant[]
  /** Optional list of tightened versions of the *question itself* (not the
   *  resolution) — e.g. adds a threshold, deadline, or concrete entity.
   *  Rendered as chips beside the normalized-question row. */
  suggested_question_rewrites?: QuestionRewrite[]
}

export type RefineResp = {
  ok: boolean
  raw_question: string
  analysis: {
    raw_question: string
    normalized_question: string
    question_type: string
    category: string
    primary_entity: string | null
    secondary_entity: string | null
    target_date: string | null
    resolution_criteria: string
    ambiguities: string[]
    options: string[]
    analysis_confidence: number
    [key: string]: any
  }
  is_categorical: boolean
  needs_confirmation: boolean
  needs_confirmation_reason?: RefineReason | null
  needs_confirmation_message?: string | null
  suggested_binary_decomposition: string[] | null
  copilot?: {
    note?: string | null
    clarification_questions?: CopilotClarification[]
    unresolved_slots?: CopilotClarification[]
    next_clarification?: CopilotClarification | null
    suggested_normalizations?: string[]
    suggested_binary_templates?: string[]
    proposed_resolution?: ProposedResolution | null
    /** True when the drafter ran but couldn't produce a confident
     *  resolution for this question; the UI should show a friendly
     *  hint rather than the (suppressed) legacy dropdown. */
    resolution_draft_empty?: boolean
  } | null
  scenario: any | null
}

export type AskQuestionResp = {
  ok: boolean
  job_id: string
  item_id: string
  question_id: number
  analysis: {
    raw_question: string
    normalized_question: string
    question_type: string
    category: string
    primary_entity: string | null
    target_date: string | null
    resolution_criteria: string
    analysis_confidence: number
    [key: string]: any
  }
}

export type AskScenario = {
  label?: string | null
  assumption_mode?: 'hard'
  assumptions: { id?: string | null; text: string }[]
  /** When set, the server binds the question to an existing first-class
   *  scenario instead of resolve-or-create'ing from (label, assumptions). */
  scenario_id?: string | null
}

export type ItemTrackPoint = {
  as_of: string | null
  update_type: string
  probability: number | null
  confidence: number | null
  confidence_interval: [number | null, number | null] | null
  market_prob: number | null
  run_id: string | null
}

export type ItemTrack = {
  kind: 'baseline' | 'scenario'
  scenario_id: string | null
  label: string | null
  description?: string | null
  status?: string | null
  assumptions: { id?: string | null; text: string }[]
  updates: ItemTrackPoint[]
  latest: ItemTrackPoint | null
}

export type ItemTracksResp = {
  item_id: string
  canonical_question_id: number | null
  tracks: ItemTrack[]
}

export type ScenarioDetail = {
  scenario_id: string
  org_id: string
  label: string
  description?: string | null
  assumptions: { id?: string | null; text: string }[]
  conditioning_guidance?: Record<string, any> | null
  tags?: string[] | null
  status: 'hypothetical' | 'active' | 'realized' | 'disproven' | 'archived' | string
  visibility: string
  created_by_account_id?: string | null
  created_at?: string | null
  updated_at?: string | null
  resolved_at?: string | null
  question_count?: number
}

export type ScenarioLinkedQuestion = {
  question_id: number
  title: string | null
  status: string | null
  item_id: string | null
  attached_at: string | null
  latest_conditional_run_id: number | null
}

export type ScenarioDossier = {
  id?: number
  scenario_id: string
  prompt_safe_summary?: string | null
  prompt_safe_key_points?: string[] | null
  prompt_safe_drivers?: string[] | null
  prompt_safe_inhibitors?: string[] | null
  prompt_safe_sources?: Array<Record<string, any>> | null
  assumption_status_json?: Record<string, any> | null
  internal_state_json?: Record<string, any> | null
  last_refresh_run_id?: number | null
  created_at?: string | null
  updated_at?: string | null
}

export type ScenarioDossierUpdate = {
  id: number
  scenario_id: string
  run_id: number | null
  update_type: string
  as_of: string | null
  prompt_safe_update?: Record<string, any> | null
  internal_update?: Record<string, any> | null
}

export type ScenarioSuggestion = ScenarioDetail & {
  label_similarity: number
  assumption_jaccard: number
  match_reason: string
}

export type ScenarioPreviewResp = {
  ok: boolean
  label?: string | null
  assumption_count?: number
  conditioning_guidance: {
    conditioning_summary?: string
    fixed_facts?: string[]
    removed_uncertainties?: string[]
    forecast_instructions?: string[]
    implication_prompts?: string[]
    target_question?: string
    [key: string]: any
  }
}

export type AskSourceRef = {
  type: 'url' | 'text' | 'note'
  value: string
  label?: string | null
}

export type AskQuestionSetResp = {
  ok: boolean
  set_id: string
  job_id: string
  watchlist_id: string
  source_count: number
  max_questions: number
}

export type QuestionSetStatus = {
  set_id: string
  status: string
  watchlist_id?: string | null
  job_id?: string | null
  generated_count: number
  forecasted_count: number
  failed_count: number
  error?: string | null
  summary_json?: any
  sources: any[]
  items: any[]
  created_at?: string | null
  updated_at?: string | null
  completed_at?: string | null
}

/** Output shape for a single-question forecast job. */
export type ForecastJobOutput = {
  probability?: number
  confidence?: number
  summary?: string
  run_id?: number
}

/** Output shape for a forecast_source_set job. */
export type ForecastSetJobOutput = {
  generated?: number
  succeeded?: number
  failed?: number
  source_count?: number
  set_id?: string
}

/**
 * Union of all job output payloads. The backend returns different keys
 * depending on `kind`, but we keep a single type so callers can narrow on
 * the relevant key with `in` / optional chaining instead of branching on
 * `kind` at every access site.
 */
export type JobOutput = ForecastJobOutput & ForecastSetJobOutput

export type JobStatus = {
  job_id: string
  status: 'queued' | 'running' | 'succeeded' | 'failed'
  kind: string
  item_id: string | null
  set_id?: string | null
  output_json: JobOutput | null
  error: string | null
  started_at: string | null
  ended_at: string | null
  created_at: string | null
}

export type JobListItem = {
  job_id: string
  kind: string
  status: 'queued' | 'running' | 'succeeded' | 'failed'
  item_id: string | null
  set_id?: string | null
  watchlist_id?: string | null
  display_question?: string | null
  output_json: JobOutput | null
  error: string | null
  started_at: string | null
  ended_at: string | null
  created_at: string | null
}

export type JobListResp = {
  count: number
  jobs: JobListItem[]
}

/** Small helper types for UI branching. */
export function isSetJob(job: Pick<JobListItem, 'kind'> | Pick<JobStatus, 'kind'>): boolean {
  return String(job?.kind || '') === 'forecast_source_set'
}
export function isForecastJob(job: Pick<JobListItem, 'kind'> | Pick<JobStatus, 'kind'>): boolean {
  return String(job?.kind || '') === 'forecast'
}

export type ReviewPatch = {
  patch_id: string
  org_id: string
  watchlist_id?: string | null
  item_id?: string | null
  canonical_question_id: number
  scored_question_id: number
  reviewed_run_id: number
  status?: string | null
  ai_review_json?: any
  prompt_safe_guidance?: string | null
  suggested_search_queries?: string[] | null
  internal_notes?: string | null
  guidance_dossier_update_id?: number | null
  rerun_job_id?: string | null
  rerun_run_id?: number | null
  created_at?: string | null
  updated_at?: string | null
}

export type ReviewPatchListResp = {
  org_id: string
  count: number
  patches: ReviewPatch[]
}

export type CompanyTrack = {
  track_id: string
  org_id: string
  company_id: number
  notify_signals: boolean
  notify_reports: boolean
  auto_add_questions: boolean
  status: string
  company?: {
    id: number
    ticker: string
    company_name: string
    sector?: string | null
    industry?: string | null
    market_cap_usd?: number | null
    last_research_at?: string | null
  }
  latest_report_summary?: string | null
  current_view_summary?: string | null
  what_changed_summary?: string | null
  approved_question_count?: number | null
  forecasted_question_count?: number | null
  watchpoint_count?: number | null
  memo_call?: any | null
  forecast_posture?: any | null
  attention_items?: any[] | null
  memo_generated_at?: string | null
  stale_status?: string | null
  last_meaningful_update_at?: string | null
  artifact?: CompanyArtifact | null
  card_summary?: CompanyCardSummary | null
}

export type CompanyGroup = {
  group_id: string
  org_id?: string | null
  name: string
  description?: string | null
  is_public: boolean
  status: string
}

export type CompanyReportSummary = {
  id: number
  company_id: number
  executive_summary?: string | null
  scenarios?: any
  signals_dashboard?: any
  report_type: string
  generated_at?: string | null
}

export type QuestionForecast = {
  probability: number
  run_id: number
  forecasted_at: string | null
}

export type CompanyArtifact = {
  current_view?: string | string[] | null
  what_changed?: string | string[] | null
  why_it_matters?: string | string[] | null
  key_questions?: any[] | null
  watchpoints?: any[] | null
  recent_signals?: any[] | null
  latest_update_at?: string | null
  artifact_quality?: number | string | null
  [key: string]: any
}

export type CompanyDeltaSummary = {
  changed_assessment?: string | string[] | null
  unchanged_core_thesis?: string | string[] | null
  new_signals?: any[] | null
  new_evidence_urls?: string[] | null
  affected_questions?: any[] | null
  next_watchpoints?: any[] | null
  what_changed?: string | string[] | null
  [key: string]: any
}

export type CompanyQuestionStatusCounts = {
  proposed?: number
  approved?: number
  active?: number
  live?: number
  rejected?: number
  retired?: number
  total?: number
  [key: string]: number | undefined
}

export type CompanyHealth = {
  last_successful_research_at?: string | null
  last_monitor_hit_at?: string | null
  overdue_monitor_count?: number | null
  approved_question_count?: number | null
  proposed_question_count?: number | null
  stale_status?: string | null
  [key: string]: any
}

export type CompanyCardSummary = {
  current_view_summary?: string | null
  what_changed_summary?: string | null
  approved_question_count?: number | null
  forecasted_question_count?: number | null
  watchpoint_count?: number | null
  memo_call?: any | null
  forecast_posture?: any | null
  attention_items?: any[] | null
  memo_generated_at?: string | null
  stale_status?: string | null
  last_meaningful_update_at?: string | null
  [key: string]: any
}

export type CompanyGeneratedQuestion = {
  id: number
  title: string
  category?: string | null
  description?: string | null
  close_ts?: string | null
  status?: string | null
  forecast?: QuestionForecast | null
  question_spec_json?: any
  resolvability_score?: number | null
  source_quality_score?: number | null
  business_value_score?: number | null
  template_risk?: string | null
  review_note?: string | null
  reviewed_at?: string | null
  [key: string]: any
}

export type CompanyQuestionModerationPayload = {
  status: 'proposed' | 'approved' | 'rejected' | 'retired'
  note?: string | null
}

export type CompanyViewer = {
  is_cenva_staff?: boolean
  can_moderate_questions?: boolean
}

export type CompanyDetail = {
  company: any
  track: any
  report: any
  baseline_report?: CompanyReportSummary | null
  monitor_update?: CompanyReportSummary | null
  forecast_review?: CompanyReportSummary | null
  memo_artifact?: any | null
  report_history?: CompanyReportSummary[]
  signals: any[]
  questions: CompanyGeneratedQuestion[]
  approved_questions?: CompanyGeneratedQuestion[]
  proposed_questions?: CompanyGeneratedQuestion[]
  inactive_questions?: CompanyGeneratedQuestion[]
  artifact?: CompanyArtifact | null
  delta_summary?: CompanyDeltaSummary | null
  question_status_counts?: CompanyQuestionStatusCounts | null
  watchpoints?: any[] | null
  company_health?: CompanyHealth | null
  card_summary?: CompanyCardSummary | null
  viewer?: CompanyViewer | null
  monitors: any[]
  research_runs: any[]
}

export type WorkspaceUniverseEntity = {
  ticker?: string | null
  name?: string | null
  relationship?: 'holding' | 'benchmark' | 'peer' | 'example'
  candidate_disposition?: 'required' | 'eligible' | 'reference_only' | 'excluded'
  thesis?: string | null
}

export type WorkspaceUniversePolicy = {
  schema_version?: string
  candidate_source: 'supplied_only' | 'supplied_and_discovered' | 'discovered_only'
  candidate_scope?: string | null
  shortlist_limit?: number
  supplied_entities?: WorkspaceUniverseEntity[]
}

export type WorkspaceDraft = {
  title: string
  original_question: string
  workspace_type: string
  horizon: string
  assumptions: string[]
  entities: any[]
  drivers: any[]
  scoping_artifacts?: any[]
  forecast_questions: any[]
  watchpoints: any[]
  report_outline: string[]
  missing_inputs: string[]
  source_plan: string[]
  layer_model?: any
  execution_plan?: any
  discovery_context?: any
  universe_policy?: WorkspaceUniversePolicy
  compiler_version: string
}

export type WorkspaceDiscovery = {
  version: string
  stage: string
  original_question: string
  context?: string | null
  horizon?: string | null
  max_questions?: number
  archetype?: any
  interaction_mode?: string | null
  guided_intake?: any
  scenario_frame?: any
  decision_frame?: any
  clarifying_questions?: any[]
  answers?: Record<string, any>
  next_question?: any | null
  next_action?: string | null
  compiled_draft_title?: string | null
  compiled_at?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export type WorkspaceDraftResp = {
  ok: boolean
  draft: WorkspaceDraft
}

export type WorkspaceCreateResp = {
  ok: boolean
  workspace_id: string
  set_id: string
  watchlist_id: string
  draft: WorkspaceDraft
}

export type WorkspaceDiscoveryStartResp = {
  ok: boolean
  workspace_id: string
  set_id: string
  watchlist_id: string
  discovery: WorkspaceDiscovery
  draft?: WorkspaceDraft | null
}

export type WorkspaceDiscoveryAnswerResp = {
  ok: boolean
  workspace_id: string
  discovery: WorkspaceDiscovery
}

export type WorkspaceDiscoveryPlanResp = {
  ok: boolean
  workspace_id: string
  discovery: WorkspaceDiscovery
  draft: WorkspaceDraft
}

/**
 * Honest, client-safe explanation for a workspace that is stuck, was never
 * built, or is being reworked after a failure. `null` when healthy.
 *
 * `message` and `action` are written by the API in plain language and never
 * contain internal pipeline/gate codes — render them verbatim.
 */
export type WorkspaceClientState = {
  kind: 'draft_stalled' | 'setup_incomplete' | 'being_reworked' | string
  message: string
  action?: string | null
}

export type WorkspaceListItem = {
  workspace_id: string
  set_id: string
  title: string
  display_title?: string | null
  display_subtitle?: string | null
  original_question?: string | null
  workspace_type?: string | null
  status: string
  horizon?: string | null
  generated_count: number
  forecasted_count: number
  failed_count: number
  answer_state?: string | null
  readiness_status?: string | null
  publication_status?: string | null
  lifecycle_status?: string | null
  active_monitor_count?: number | null
  next_monitor_run_at?: string | null
  headline?: string | null
  created_at?: string | null
  updated_at?: string | null
  last_viewed_at?: string | null
  latest_change_at?: string | null
  latest_change_summary?: string | null
  changed_since_view?: boolean
  client_state?: WorkspaceClientState | null
}

export type WorkspaceListResp = {
  workspaces: WorkspaceListItem[]
}

export type WorkspaceDetail = {
  workspace_id: string
  set_id: string
  status: string
  watchlist_id?: string | null
  draft: WorkspaceDraft
  discovery?: WorkspaceDiscovery | null
  question_set: any
  sources: any[]
  items: any[]
  created_at?: string | null
  updated_at?: string | null
  client_state?: WorkspaceClientState | null
}

export type WorkspaceGraphResp = {
  ok: boolean
  workspace_id: string
  graph: any
}

export type WorkspaceExecuteResp = {
  ok: boolean
  workspace_id: string
  status: string
  generated_count: number
  forecast_job_count: number
  skipped_count: number
  watchlist_id?: string | null
  items: any[]
  pipeline?: any | null
}

export type WorkspaceActivateResp = {
  ok: boolean
  workspace_id: string
  status: string
  monitor_count: number
  next_monitor_run_at?: string | null
  lifecycle: any
}

export type WorkspaceLayer0Resp = {
  ok: boolean
  workspace_id: string
  // scoping_ready (stored result) | scoping_queued | scoping_in_progress
  status: string
  job_id?: string | null
  shortlist_count?: number
  coverage_count?: number
  snapshot_count?: number
  layer0?: any
}

const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL || ''

type ApiFetchInit = RequestInit & {
  timeoutMs?: number
}

// Paths that must never receive the session Authorization header.
// These are reached before a session exists (or intentionally operate
// without one); attaching a stale token can trigger server-side 401s.
const AUTH_FREE_PATH_PREFIXES = [
  '/v1/auth/start',
  '/v1/auth/verify',
  '/v1/invites/preview',
]

function pathRequiresAuth(path: string): boolean {
  return !AUTH_FREE_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))
}

function authHeaders(path: string): Record<string, string> {
  if (!pathRequiresAuth(path)) return {}
  const token = localStorage.getItem('cenva_intel_session_token')
  if (!token) return {}
  return { Authorization: `Bearer ${token}` }
}

/** Convert raw server error text to a user-safe message. */
function userSafeErrorMessage(status: number, statusText: string, body: string, path: string): string {
  if (status === 401 && pathRequiresAuth(path)) return 'Your session expired. Please sign in again.'
  // Try to extract JSON { detail } error payloads first.
  const trimmed = (body || '').trim()
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed)
      const detail = parsed?.detail || parsed?.error || parsed?.message
      if (typeof detail === 'string' && detail.trim()) return detail.trim()
    } catch { /* fall through */ }
  }
  if (status === 401 || status === 403) return 'You are not signed in or lack permission for this action.'
  if (status === 404) return 'Not found.'
  if (status === 429) return 'Rate limited. Please wait a moment and try again.'
  if (status >= 500) return 'The server is having trouble. Please try again shortly.'
  // Fallback: use statusText (never raw body, which may include stack traces).
  return statusText || `Request failed (${status})`
}

async function apiFetch<T>(path: string, init?: ApiFetchInit): Promise<T> {
  const url = `${API_BASE}${path}`
  const timeoutMs: number | undefined = typeof init?.timeoutMs === 'number' && init.timeoutMs > 0 ? init.timeoutMs : undefined
  const controller = timeoutMs !== undefined ? new AbortController() : null
  const timeoutId = controller && timeoutMs !== undefined ? window.setTimeout(() => controller.abort(), timeoutMs) : null

  try {
    const res = await fetch(url, {
      ...init,
      signal: controller?.signal || init?.signal,
      // Always include cookies so the HttpOnly session cookie is sent on
      // every request. Combined with CORS allow_credentials=true on the
      // server, this is the primary auth path now; the Authorization
      // header below is only used for pre-cookie sessions still in
      // localStorage.
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers || {}),
        ...authHeaders(path),
      },
    })
    if (!res.ok) {
      const txt = await res.text().catch(() => '')
      if (res.status === 401 && pathRequiresAuth(path)) expireSession('api_401')
      const msg = userSafeErrorMessage(res.status, res.statusText, txt, path)
      const err = new Error(msg) as Error & { status?: number; rawBody?: string }
      err.status = res.status
      err.rawBody = txt
      throw err
    }
    return (await res.json()) as T
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.')
    }
    throw err
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId)
  }
}

export const clientApi = {
  authStart: (email: string, invite_code?: string | null) =>
    apiFetch<AuthStartResp>('/v1/auth/start', {
      method: 'POST',
      timeoutMs: 20000,
      body: JSON.stringify({ email, ...(invite_code ? { invite_code } : {}) }),
    }),
  authVerify: (email: string, token: string, invite_code?: string | null, org_name?: string | null, org_slug?: string | null) =>
    apiFetch<AuthVerifyResp>('/v1/auth/verify', {
      method: 'POST',
      timeoutMs: 15000,
      body: JSON.stringify({
        email,
        token,
        ...(invite_code ? { invite_code } : {}),
        ...(org_name ? { org_name } : {}),
        ...(org_slug ? { org_slug } : {}),
      }),
    }),
  authLogout: () =>
    apiFetch<AuthLogoutResp>('/v1/auth/logout', {
      method: 'POST',
      timeoutMs: 5000,
    }),
  inviteAccept: (invite_code: string, org_name?: string | null, org_slug?: string | null) =>
    apiFetch<InviteAcceptResp>('/v1/invites/accept', {
      method: 'POST',
      timeoutMs: 15000,
      body: JSON.stringify({
        invite_code,
        ...(org_name ? { org_name } : {}),
        ...(org_slug ? { org_slug } : {}),
      }),
    }),
  invitePreview: (invite_code: string) =>
    apiFetch<InvitePreviewResp>('/v1/invites/preview', {
      method: 'POST',
      timeoutMs: 10000,
      body: JSON.stringify({ invite_code }),
    }),
  me: () => apiFetch<MeResp>('/v1/me', { timeoutMs: 10000 }),
  recordUsageEvent: (payload: {
    action: string
    target_type?: string | null
    target_id?: string | null
    meta_json?: Record<string, any> | null
  }) =>
    apiFetch<{ ok: boolean }>('/v1/usage/event', {
      method: 'POST',
      timeoutMs: 5000,
      body: JSON.stringify(payload),
    }),

  orgSearch: (q: string, limit?: number) => {
    const qs = new URLSearchParams()
    qs.set('q', q || '')
    if (typeof limit === 'number') qs.set('limit', String(limit))
    return apiFetch<OrgSearchResp>(`/v1/orgs/search?${qs.toString()}`)
  },

  setActiveOrg: (org_id: string) =>
    apiFetch<ActiveOrgSwitchResp>('/v1/session/active-org', {
      method: 'POST',
      body: JSON.stringify({ org_id }),
    }),

  org: () => apiFetch<any>('/v1/org'),
  updateOrg: (name?: string | null, slug?: string | null) =>
    apiFetch<OrgUpdateResp>('/v1/org', {
      method: 'PATCH',
      body: JSON.stringify({ ...(name ? { name } : {}), ...(slug ? { slug } : {}) }),
    }),

  packs: () => apiFetch<Pack[]>('/v1/packs'),
  selectPacks: (pack_ids: string[]) => apiFetch<any>('/v1/onboarding/select-packs', { method: 'POST', body: JSON.stringify({ pack_ids }) }),

  watchlists: () => apiFetch<any[]>('/v1/watchlists'),
  watchlistDetail: (watchlist_id: string) => apiFetch<WatchlistDetail>(`/v1/watchlists/${watchlist_id}`),
  watchlistSummary: (watchlist_id: string) => apiFetch<WatchlistSummary>(`/v1/watchlists/${watchlist_id}/summary`),
  feed: (watchlist_id: string) => apiFetch<any>(`/v1/watchlists/${watchlist_id}/feed`),
  itemDetail: (item_id: string) => apiFetch<any>(`/v1/items/${item_id}`),
  itemTracks: (item_id: string) => apiFetch<ItemTracksResp>(`/v1/items/${item_id}/tracks`),

  previewScenario: (scenario: AskScenario, target_question?: string | null) =>
    apiFetch<ScenarioPreviewResp>('/v1/scenarios/preview', {
      method: 'POST',
      body: JSON.stringify({
        scenario,
        ...(target_question ? { target_question } : {}),
      }),
    }),

  // First-class scenarios CRUD (Phase D).
  listScenarios: (opts?: { includeArchived?: boolean }) =>
    apiFetch<{ ok: boolean; scenarios: ScenarioDetail[] }>(
      `/v1/scenarios${opts?.includeArchived ? '?include_archived=true' : ''}`,
    ),
  getScenario: (scenario_id: string) =>
    apiFetch<{
      ok: boolean
      scenario: ScenarioDetail
      questions: ScenarioLinkedQuestion[]
    }>(`/v1/scenarios/${scenario_id}`),
  createScenarioEntity: (body: {
    label: string
    description?: string | null
    assumptions: { id?: string | null; text: string }[]
    tags?: string[] | null
    force_new?: boolean
  }) =>
    apiFetch<{
      ok: boolean
      scenario: ScenarioDetail
      // True when the server matched an existing scenario (same label +
      // identical assumption texts) and returned that instead of creating
      // a duplicate. Only set when the request was made with
      // `force_new: false` (the default).
      reused?: boolean
      suggestions: ScenarioSuggestion[]
    }>('/v1/scenarios', { method: 'POST', body: JSON.stringify(body) }),
  searchScenarios: (body: {
    label: string
    assumptions: { id?: string | null; text: string }[]
  }) =>
    apiFetch<{ ok: boolean; suggestions: ScenarioSuggestion[] }>(
      '/v1/scenarios/search',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateScenario: (
    scenario_id: string,
    body: Partial<{
      label: string
      description: string | null
      assumptions: { id?: string | null; text: string }[]
      tags: string[] | null
      status: 'hypothetical' | 'active' | 'realized' | 'disproven' | 'archived'
    }>,
  ) =>
    apiFetch<{
      ok: boolean
      scenario: ScenarioDetail
      changed: boolean
      queued_refresh_job_id?: string | null
      queued_reforecast_job_id?: string | null
    }>(`/v1/scenarios/${scenario_id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  archiveScenario: (scenario_id: string) =>
    apiFetch<{ ok: boolean; scenario_id: string; status: string }>(
      `/v1/scenarios/${scenario_id}`,
      { method: 'DELETE' },
    ),
  getScenarioDossier: (scenario_id: string) =>
    apiFetch<{
      ok: boolean
      scenario_id: string
      dossier: ScenarioDossier | null
      updates: ScenarioDossierUpdate[]
    }>(`/v1/scenarios/${scenario_id}/dossier`),
  refreshScenarioDossier: (scenario_id: string) =>
    apiFetch<{ ok: boolean; scenario_id: string; dossier: ScenarioDossier | null }>(
      `/v1/scenarios/${scenario_id}/refresh`,
      { method: 'POST' },
    ),
  reforecastScenario: (scenario_id: string) =>
    apiFetch<{ ok: boolean; scenario_id: string; job_id: string }>(
      `/v1/scenarios/${scenario_id}/reforecast-all`,
      { method: 'POST' },
    ),
  refineQuestion: (question: string, scenario?: AskScenario | null) =>
    apiFetch<RefineResp>('/v1/questions/refine', {
      method: 'POST',
      body: JSON.stringify({
        question,
        ...(scenario ? { scenario } : {}),
      }),
    }),
  askQuestion: (question: string, watchlist_id?: string, scenario?: AskScenario | null, client_meta?: ClientMeta | null) =>
    apiFetch<AskQuestionResp>('/v1/questions/ask', {
      method: 'POST',
      body: JSON.stringify({
        question,
        ...(watchlist_id ? { watchlist_id } : {}),
        ...(scenario ? { scenario } : {}),
        ...(client_meta ? { client_meta } : {}),
      }),
    }),
  askQuestionSet: (payload: {
    watchlist_id?: string
    prompt?: string
    max_questions?: number
    scenario?: AskScenario | null
    client_meta?: ClientMeta | null
    sources: AskSourceRef[]
  }) =>
    apiFetch<AskQuestionSetResp>('/v1/questions/ask-set', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  questionSetStatus: (set_id: string) => apiFetch<QuestionSetStatus>(`/v1/question-sets/${set_id}`),
  draftWorkspace: (payload: { question: string; context?: string | null; horizon?: string | null; max_questions?: number; universe_policy?: WorkspaceUniversePolicy | null }) =>
    apiFetch<WorkspaceDraftResp>('/v1/workspaces/draft', {
      method: 'POST',
      timeoutMs: 20000,
      body: JSON.stringify(payload),
    }),
  createWorkspace: (payload: { question: string; context?: string | null; horizon?: string | null; max_questions?: number; watchlist_id?: string | null; universe_policy?: WorkspaceUniversePolicy | null }) =>
    apiFetch<WorkspaceCreateResp>('/v1/workspaces', {
      method: 'POST',
      timeoutMs: 20000,
      body: JSON.stringify(payload),
    }),
  startWorkspaceDiscovery: (payload: { question: string; context?: string | null; horizon?: string | null; max_questions?: number; watchlist_id?: string | null; universe_policy?: WorkspaceUniversePolicy | null }) =>
    apiFetch<WorkspaceDiscoveryStartResp>('/v1/workspaces/discovery/start', {
      method: 'POST',
      timeoutMs: 20000,
      body: JSON.stringify(payload),
    }),
  answerWorkspaceDiscovery: (workspace_id: string, payload: { answers: Record<string, any> }) =>
    apiFetch<WorkspaceDiscoveryAnswerResp>(`/v1/workspaces/${encodeURIComponent(workspace_id)}/discovery/answer`, {
      method: 'POST',
      timeoutMs: 20000,
      body: JSON.stringify(payload),
    }),
  planWorkspaceDiscovery: (workspace_id: string, payload: { max_questions?: number } = {}) =>
    apiFetch<WorkspaceDiscoveryPlanResp>(`/v1/workspaces/${encodeURIComponent(workspace_id)}/discovery/plan`, {
      method: 'POST',
      timeoutMs: 20000,
      body: JSON.stringify(payload),
    }),
  scopeWorkspace: (workspace_id: string, payload: { force?: boolean } = {}) =>
    apiFetch<WorkspaceLayer0Resp>(`/v1/workspaces/${encodeURIComponent(workspace_id)}/scope`, {
      method: 'POST',
      timeoutMs: 30000,
      body: JSON.stringify(payload),
    }),
  refreshWorkspace: (workspace_id: string) =>
    apiFetch<{ ok: boolean; workspace_id: string; status: string; job_id?: string | null }>(
      `/v1/workspaces/${encodeURIComponent(workspace_id)}/refresh`,
      { method: 'POST', timeoutMs: 20000, body: JSON.stringify({}) },
    ),
  reviewWorkspaceShortlist: (workspace_id: string, payload: { tickers?: string[]; candidates?: any[] }) =>
    apiFetch<WorkspaceLayer0Resp>(`/v1/workspaces/${encodeURIComponent(workspace_id)}/shortlist`, {
      method: 'POST',
      timeoutMs: 30000,
      body: JSON.stringify(payload),
  }),
  workspaces: () => apiFetch<WorkspaceListResp>('/v1/workspaces'),
  workspaceDetail: (workspace_id: string) => apiFetch<WorkspaceDetail>(`/v1/workspaces/${encodeURIComponent(workspace_id)}?compact=1`),
  workspaceGraph: (workspace_id: string) => apiFetch<WorkspaceGraphResp>(`/v1/workspaces/${encodeURIComponent(workspace_id)}/graph?mode=cockpit`),
  executeWorkspace: (workspace_id: string, payload: { enqueue_forecasts?: boolean; execution_mode?: 'live' | 'batch'; model?: 'claude-sonnet-5' | 'claude-sonnet-4-6' | 'claude-opus-4-6'; force?: boolean; pipeline?: boolean; wait_for_batch?: boolean; activate?: boolean; budget_profile?: string }) =>
    apiFetch<WorkspaceExecuteResp>(`/v1/workspaces/${encodeURIComponent(workspace_id)}/execute`, {
      method: 'POST',
      // Live materialization generates the forecast questions inline before
      // queueing forecast jobs — comfortably over 30s for 20+ questions.
      timeoutMs: 180000,
      body: JSON.stringify(payload),
    }),
  activateWorkspace: (workspace_id: string) =>
    apiFetch<WorkspaceActivateResp>(`/v1/workspaces/${encodeURIComponent(workspace_id)}/activate`, {
      method: 'POST',
      timeoutMs: 30000,
    }),
  jobStatus: (job_id: string) => apiFetch<JobStatus>(`/v1/jobs/${job_id}`),
  jobs: (kind?: string, limit = 30, askOnly = false) => {
    const qs = new URLSearchParams()
    if (kind) qs.set('kind', kind)
    qs.set('limit', String(limit))
    if (askOnly) qs.set('ask_only', 'true')
    return apiFetch<JobListResp>(`/v1/jobs?${qs.toString()}`)
  },

  // Cenva staff: forecast review patches
  listReviewPatches: (opts?: { status?: string; reviewed_run_id?: number; limit?: number }) => {
    const qs = new URLSearchParams()
    if (opts?.status) qs.set('status', String(opts.status))
    if (typeof opts?.reviewed_run_id === 'number') qs.set('reviewed_run_id', String(opts.reviewed_run_id))
    if (typeof opts?.limit === 'number') qs.set('limit', String(opts.limit))
    return apiFetch<ReviewPatchListResp>(`/v1/review-patches?${qs.toString()}`)
  },
  createReviewPatch: (item_id: string, reviewed_run_id?: number) =>
    apiFetch<{ ok: boolean; patch: ReviewPatch }>('/v1/review-patches', {
      method: 'POST',
      body: JSON.stringify({ item_id, ...(typeof reviewed_run_id === 'number' ? { reviewed_run_id } : {}) }),
    }),
  updateReviewPatch: (patch_id: string, payload: Partial<ReviewPatch>) =>
    apiFetch<{ ok: boolean; patch: ReviewPatch }>(`/v1/review-patches/${patch_id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  applyReviewGuidance: (patch_id: string, payload: { prompt_safe_guidance?: string | null; suggested_search_queries?: string[] | null }) =>
    apiFetch<{ ok: boolean; patch: ReviewPatch; guidance_dossier_update_id?: number | null }>(`/v1/review-patches/${patch_id}/apply-guidance`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  enqueueReviewAi: (patch_id: string) =>
    apiFetch<{ ok: boolean; job_id: string; patch_id: string }>(`/v1/review-patches/${patch_id}/ai-review`, { method: 'POST' }),
  enqueueReviewRerun: (patch_id: string) =>
    apiFetch<{ ok: boolean; job_id: string; patch: ReviewPatch }>(`/v1/review-patches/${patch_id}/rerun`, { method: 'POST' }),

  // Companies
  companies: () => apiFetch<{ companies: CompanyTrack[] }>('/v1/companies'),
  companyDetail: (id: number) => apiFetch<CompanyDetail>(`/v1/companies/${id}`),
  trackCompany: (ticker?: string, company_id?: number) =>
    apiFetch<any>('/v1/companies/track', {
      method: 'POST',
      body: JSON.stringify({ ...(ticker ? { ticker } : {}), ...(company_id ? { company_id } : {}) }),
    }),
  untrackCompany: (id: number) =>
    apiFetch<any>(`/v1/companies/${id}/track`, { method: 'DELETE' }),
  refreshCompany: (id: number) =>
    apiFetch<any>(`/v1/companies/${id}/refresh`, { method: 'POST' }),
  forecastCompany: (id: number, opts?: { new_only?: boolean }) =>
    apiFetch<{ ok: boolean; job_id: string; question_count: number; new_only?: boolean }>(`/v1/companies/${id}/forecast`, {
      method: 'POST',
      ...(opts?.new_only ? { body: JSON.stringify({ new_only: true }) } : {}),
    }),
  updateCompanyQuestion: (company_id: number, question_id: number, payload: CompanyQuestionModerationPayload) =>
    apiFetch<{ ok: boolean; question: CompanyGeneratedQuestion }>(`/v1/companies/${company_id}/questions/${question_id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  // Company groups
  companyGroups: () => apiFetch<{ groups: CompanyGroup[] }>('/v1/companies/groups'),
  createCompanyGroup: (name: string, description?: string) =>
    apiFetch<any>('/v1/companies/groups', {
      method: 'POST',
      body: JSON.stringify({ name, ...(description ? { description } : {}) }),
    }),
  companyGroupDetail: (id: string) => apiFetch<{ group: CompanyGroup; members: any[] }>(`/v1/companies/groups/${id}`),
  updateGroupMembers: (id: string, add: number[], remove: number[]) =>
    apiFetch<any>(`/v1/companies/groups/${id}/members`, {
      method: 'POST',
      body: JSON.stringify({ add_company_ids: add, remove_company_ids: remove }),
    }),
  selectGroups: (ids: string[]) =>
    apiFetch<any>('/v1/onboarding/select-groups', {
      method: 'POST',
      body: JSON.stringify({ group_ids: ids }),
    }),

  // Onboarding
  onboardingComplete: () =>
    apiFetch<{ ok: boolean; org: any }>('/v1/onboarding/complete', { method: 'POST' }),

  // Run detail (forecast reasoning)
  runDetail: (run_id: number) => apiFetch<any>(`/v1/runs/${run_id}`),

  // Delivery
  triggerDelivery: (subscription_id: string) =>
    apiFetch<any>(`/v1/delivery/trigger?subscription_id=${encodeURIComponent(subscription_id)}`, { method: 'POST' }),

  // PDF report downloads (Cenva Intelligence)
  downloadCompanyReport: (companyId: number) =>
    downloadReportPdf(`/v1/companies/${companyId}/report.pdf`, `cenva-company-${companyId}.pdf`),
  downloadQuestionReport: (itemId: string) =>
    downloadReportPdf(`/v1/items/${encodeURIComponent(itemId)}/report.pdf`, `cenva-question-${itemId}.pdf`),
  downloadQuestionSetReport: (setId: string) =>
    downloadReportPdf(
      `/v1/question-sets/${encodeURIComponent(setId)}/report.pdf`,
      `cenva-source-set-${setId}.pdf`,
    ),
  downloadWorkspaceReport: (workspaceId: string) =>
    downloadReportPdf(
      `/v1/workspaces/${encodeURIComponent(workspaceId)}/report.pdf`,
      `cenva-workspace-${workspaceId}.pdf`,
    ),
  previewCompanyReport: (companyId: number) =>
    openReportHtmlPreview(`/v1/companies/${companyId}/report.html`),
  previewQuestionReport: (itemId: string) =>
    openReportHtmlPreview(`/v1/items/${encodeURIComponent(itemId)}/report.html`),
  previewQuestionSetReport: (setId: string) =>
    openReportHtmlPreview(`/v1/question-sets/${encodeURIComponent(setId)}/report.html`),
  previewWorkspaceReport: (workspaceId: string) =>
    openReportHtmlPreview(`/v1/workspaces/${encodeURIComponent(workspaceId)}/report.html`),
}

/**
 * Fetch a PDF, save it to disk under the filename suggested by the
 * server's ``Content-Disposition`` header (or the supplied fallback).
 *
 * Kept outside ``clientApi`` because it doesn't return JSON — the
 * regular ``apiFetch`` pipeline isn't a good fit for binary downloads.
 */
async function downloadReportPdf(path: string, fallbackFilename: string): Promise<void> {
  const url = `${API_BASE}${path}`
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      ...authHeaders(path),
      Accept: 'application/pdf',
    },
  })
  if (!res.ok) {
    const txt = await res.text().catch(() => '')
    const msg = userSafeErrorMessage(res.status, res.statusText, txt)
    const err = new Error(msg) as Error & { status?: number }
    err.status = res.status
    throw err
  }
  const blob = await res.blob()
  const cd = res.headers.get('Content-Disposition') || ''
  const match = /filename="?([^";]+)"?/.exec(cd)
  const filename = match ? match[1] : fallbackFilename
  const objectUrl = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
  }
}

async function openReportHtmlPreview(path: string): Promise<void> {
  const preview = window.open('', '_blank')
  if (!preview) {
    throw new Error('Popup blocked. Allow pop-ups to preview the report.')
  }
  try {
    preview.opener = null
    preview.document.write('<!doctype html><title>Loading report preview...</title><p>Loading report preview...</p>')
    preview.document.close()
  } catch {
    // Best-effort loading state only.
  }

  const url = `${API_BASE}${path}`
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      ...authHeaders(path),
      Accept: 'text/html',
    },
  })
  if (!res.ok) {
    try {
      preview.close()
    } catch {
      // Ignore close failures; the user still gets the inline error.
    }
    const txt = await res.text().catch(() => '')
    const msg = userSafeErrorMessage(res.status, res.statusText, txt)
    const err = new Error(msg) as Error & { status?: number }
    err.status = res.status
    throw err
  }

  const html = await res.text()
  try {
    preview.document.open()
    preview.document.write(html)
    preview.document.close()
    preview.focus()
  } catch {
    const objectUrl = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }))
    preview.location.href = objectUrl
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
  }
}
